import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { Loader2, Save, Database } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useInventoryLedgerSources } from '@/hooks/useInventoryLedgerSources';
import {
  connectionHasAmazonInventoryCsv,
  connectionHasShopifyInventoryCsv,
} from '@/shared/inventoryCsvConnectionChannels';

interface InventorySourceSettingsProps {
  organisation: any;
  onUpdate?: (config: InventorySourceConfig) => void;
}

interface InventorySourceConfig {
  primary_source: string;
  enabled_sources: string[];
}

const InventorySourceSettings: React.FC<InventorySourceSettingsProps> = ({
  organisation,
  onUpdate
}) => {
  const [primarySource, setPrimarySource] = useState<string>('all');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { data: registryRows = [], isLoading: registryLoading } = useInventoryLedgerSources(
    organisation?.id,
    Boolean(organisation?.id),
  );

  // Extract available sources from organisation connections
  const availableSources = useMemo(() => {
    const sources: { value: string; label: string }[] = [
      { value: 'all', label: 'Per-connection (Default)' }
    ];

    if (organisation?.connections) {
      const connections = Array.isArray(organisation.connections)
        ? organisation.connections
        : Object.values(organisation.connections);

      const pushUnique = (value: string, label: string) => {
        if (value && !sources.find((s) => s.value === value)) {
          sources.push({ value, label });
        }
      };

      connections.forEach((conn: any) => {
        if (!conn) return;

        // Skip file-type connections
        if (conn.service === 'file') return;

        const isCsvRow =
          conn.type === 'CSV' ||
          String(conn.type || '').toLowerCase() === 'csv' ||
          String(conn.connectionId || '').startsWith('csv-import-');

        if (isCsvRow) {
          if (connectionHasShopifyInventoryCsv(conn)) pushUnique('shopify_csv', 'Shopify CSV Import');
          if (connectionHasAmazonInventoryCsv(conn)) pushUnique('amazon_csv', 'Amazon CSV Import');
          return;
        }

        // For Fivetran connections
        const service = (conn.service || '').toLowerCase();
        if (service.includes('shopify')) {
          pushUnique('shopify', 'Shopify');
        } else if (service.includes('amazon')) {
          pushUnique('amazon', 'Amazon');
        } else if (service.includes('veeqo')) {
          pushUnique('veeqo', 'Veeqo');
        }
      });
    }

    return sources;
  }, [organisation?.connections]);

  // Load current config
  useEffect(() => {
    if (organisation?.inventory_source_config?.primary_source) {
      setPrimarySource(organisation.inventory_source_config.primary_source);
    }
  }, [organisation?.inventory_source_config]);

  const handleSave = async () => {
    if (!organisation?.id) return;

    setSaving(true);
    try {
      const config = {
        primary_source: primarySource,
        enabled_sources: availableSources.map(s => s.value).filter(v => v !== 'all')
      };

      // inventory_source_changed_at is the org-wide signal that drives the
      // "report is regenerating because the inventory source changed" banner
      // on the product report. It must land in the same UPDATE as the config
      // so any reader sees a consistent snapshot.
      const changedAt = new Date().toISOString();
      const { error } = await supabase
        .from('organisations')
        .update({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          inventory_source_config: config as any,
          inventory_source_changed_at: changedAt,
        })
        .eq('id', organisation.id);

      if (error) throw error;

      // Trigger report regeneration only for supported live connection types
      const connections = Array.isArray(organisation.connections)
        ? organisation.connections
        : Object.values(organisation.connections || {});

      const supportedTypes = ['shopify', 'amazon', 'veeqo'];

      // Filter to only live Shopify/Amazon/Veeqo connections (exclude CSV imports)
      const eligibleConnections = connections.filter((conn: any) => {
        if (!conn) return false;

        const type = (conn.type || '').toLowerCase();
        const service = (conn.service || '').toLowerCase();
        const isCsvImport = type === 'csv' || service === 'csv' || conn.connectionId?.startsWith('csv-import-');

        if (isCsvImport) {
          return false;
        }

        // Check if live connector (Shopify/Amazon)
        if (supportedTypes.some(t => type.includes(t) || service.includes(t))) {
          return true;
        }

        return false;
      });

      const uniqueConnectorIds: string[] = Array.from(new Set(
        eligibleConnections
          .map((conn: any) => conn.connectionId || conn.id || conn.metadata?.connectorId)
          .filter((connectorId): connectorId is string => typeof connectorId === 'string' && connectorId.length > 0)
      ));

      // Queue regeneration jobs server-side. The DB row is durable so closing
      // the tab cannot lose the work; the coordinator picks queued jobs up and
      // runs generate-sku-report-v6 / generate-product-report-v2 server-side
      // (it picks the right generator based on organisations.product_identifier).
      if (uniqueConnectorIds.length > 0) {
        const jobRows = uniqueConnectorIds.map((connectorId) => ({
          connector_id: connectorId,
          organisation_id: organisation.id,
          status: 'queued' as const,
        }));

        const { error: jobError } = await supabase
          .from('sku_report_jobs')
          .insert(jobRows);

        if (jobError) {
          // Non-fatal: reports-data-v10 will detect staleness on next view and
          // queue jobs via triggerBackgroundGeneration anyway.
          console.error('Failed to queue regeneration jobs:', jobError);
        } else {
          // Fire-and-forget the coordinator so it picks up the new jobs ASAP.
          // Even if this request is aborted on tab close, the queued rows
          // persist and the next reports-data-v10 view will re-invoke it.
          void invokeFunction(supabase, 'sku-report-coordinator-v3', { body: {} })
            .catch((err) => console.error('Failed to invoke coordinator:', err));
        }
      }

      toast({
        title: "Settings saved",
        description: uniqueConnectorIds.length > 0
          ? `Primary inventory source set to ${availableSources.find(s => s.value === primarySource)?.label || primarySource}. Reports will refresh in the background.`
          : `Primary inventory source set to ${availableSources.find(s => s.value === primarySource)?.label || primarySource}.`,
      });

      onUpdate?.(config);
    } catch (error) {
      console.error('Error updating inventory source config:', error);
      toast({
        title: "Save failed",
        description: "Failed to update inventory source settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const currentSourceLabel = availableSources.find(s => s.value === primarySource)?.label || 'Per-connection';

  return (
    <>
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Primary Inventory Source</CardTitle>
          </div>
          <CardDescription>
            Select the default data source for inventory calculations and NERV reports.
            This helps when multiple e-commerce connections exist but only one has accurate inventory data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="primary-source">Default Inventory Source</Label>
            <Select value={primarySource} onValueChange={setPrimarySource}>
              <SelectTrigger id="primary-source">
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                {availableSources.map((source) => (
                  <SelectItem key={source.value} value={source.value}>
                    {source.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Inventory tab and NERV graphs will default to this source.
              You can still override with filters at runtime.
            </p>
          </div>

          {availableSources.length > 1 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-muted-foreground">Connected:</span>
              {availableSources.filter(s => s.value !== 'all').map((source) => (
                <Badge
                  key={source.value}
                  variant={source.value === primarySource ? "default" : "secondary"}
                >
                  {source.label}
                </Badge>
              ))}
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </CardContent>
      </Card>

    </>
  );
};

export default InventorySourceSettings;
